/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useRef, useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Box, Typography } from '@mui/material';

import { SingleFadeLoader } from 'components/SingleFadeLoader';
import TireProfileHandler from 'handlers/tire-pressure';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import type { Asset } from 'types/asset.types';
import { type Device } from 'types/device.types';
import { composeUniqueKey } from 'utils/helpers/string.manipulation';
import { isDarkTheme } from 'utils/theme';
import { getAssetMakeModel } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { AssetDetail } from './Components/AssetDetail/Index';
import { AssetSelector } from './Components/AssetSelector';
import { Content } from './Components/Content/Index';
import { PressureLimitModalTabularView } from './Components/modal/PressureLimitTable';
import { FitmentDate } from './Components/modal/FitmentDate';
import { convertFahrToCent, convertPsiToKpa } from 'utils/helpers/unitSystem';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { formatStartEndDateUTC } from 'utils/helpers/dateTime';
import { TirePosition } from './Components/modal/TirePosition';
import { AxleWiseThresholdsModal } from './Components/modal/AxleWiseThresholdsModal';
import { getRangeDates } from './util';
import { findDeviceWithMostTpmSData } from 'utils/assets';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

const VehicleInsight = () => {
  const { t } = useTranslation();
  const tireProfileHandler = new TireProfileHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { assets, assetsLoaded } = useAppSelector(
    (state) => state.assetReducer
  );
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);

  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  const tpmsAssets = useMemo(
    () => assets.filter((item) => item.tpmsEnabled),
    [assets]
  );
  const makeModels = getAssetMakeModel(tpmsAssets);
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const hierarchyInfo = useAppSelector((state) => state.hierarchyReducer);
  const {
    fleetData,
    tireData,
    axleWiseLimits,
    isLoadingVehicle,
    isPollingVehicle,
    batteryData,
  } = useAppSelector((state) => state.tireProfileReducer);
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [selected, setSelected] = useState('Last 7 Days');
  const [initialPageLoad, setInitialPageLoad] = useState<boolean>(true);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isAxleWiseModalOpen, setIsAxleWiseModalOpen] = useState(false);
  const [isFitmentViewOpen, setIsFitmentViewOpen] = useState(false);
  const [isTirePositionOpen, setIsTirePositionOpen] = useState(false);

  const defaultAsset: string | null = new URLSearchParams(location.search).get(
    'asset'
  );

  const [defaultProfile, setDefaultProfile] = useState<string | null>(
    new URLSearchParams(location.search).get('tire')
  );

  const clearProfile = () => {
    setDefaultProfile(null);
  };

  const defaultDate: string | null = new URLSearchParams(location.search).get(
    'date'
  );

  const defaultMetricType: string | null = new URLSearchParams(
    location.search
  ).get('metric');

  const defaultTireCode: string | null = new URLSearchParams(
    location.search
  ).get('tire');

  // Present only for slow-leak notification click-throughs — the alert's own
  // end timestamp, used in place of the default 1-day window so the full,
  // multi-day leak trend isn't cut off.
  const defaultEndDate: string | null = new URLSearchParams(
    location.search
  ).get('endDate');

  const [selectedRange, setSelectedRange] = useState<string>(
    defaultDate ? 'Custom' : 'Today'
  );
  const [startDate, setStartDate] = useState<Date>(
    defaultDate ? new Date(defaultDate) : new Date()
  );
  const [endDate, setEndDate] = useState(() => {
    if (defaultEndDate) return new Date(defaultEndDate);
    const baseDate = defaultDate ? new Date(defaultDate) : new Date();
    const newDate = new Date(baseDate);
    newDate.setDate(newDate.getDate() + 1);
    return newDate;
  });

  const [selectedMakeModel, setSelectedMakeModel] = useState(() => {
    if (makeModels.length > 0) {
      return makeModels[0];
    } else {
      return '';
    }
  });

  const [assetBumperIds, setAssetBumperIds] = useState<string[]>([]);

  const [selectedAsset, setSelectedAsset] = useState<Asset>({
    assetId: '',
    assetVin: '',
    assetType: 'haul_truck',
    make: '',
    bumperNumber: '',
    engineHoursOffset: -1,
    device: {} as const as Device,
    isActive: false,
    model: '',
    odometerOffset: -1,
    fuelSource: '',
    operators: [],
    externalId: '',
    assetNotes: [],
  });

  const [comparingAsset, setComparingAsset] = useState<string>('');

  const [metricType, setMetricType] = useState<string>(
    defaultMetricType ?? 'Pressure'
  );

  // Update metricType when navigating from notifications with different metric types
  useEffect(() => {
    if (defaultMetricType) {
      setMetricType(defaultMetricType);
    }
  }, [defaultMetricType]);

  async function addPressureLimit(arg: any) {
    const payloadWithConversion = {
      ...arg,

      maxPressure:
        unitSystem === 'metric'
          ? Number(arg.maxPressure)
          : Number(convertPsiToKpa(Number(arg.maxPressure)).toFixed(2)),

      minPressure:
        unitSystem === 'metric'
          ? Number(arg.minPressure)
          : Number(convertPsiToKpa(Number(arg.minPressure)).toFixed(2)),

      warningPressure:
        unitSystem === 'metric'
          ? Number(arg.warningPressure)
          : Number(convertPsiToKpa(Number(arg.warningPressure)).toFixed(2)),

      maxTemperature:
        unitSystem === 'metric'
          ? Number(arg.maxTemperature)
          : Number(convertFahrToCent(Number(arg.maxTemperature)).toFixed(2)),

      minTemperature:
        unitSystem === 'metric'
          ? Number(arg.minTemperature)
          : Number(convertFahrToCent(Number(arg.minTemperature)).toFixed(2)),

      warningTemperature:
        unitSystem === 'metric'
          ? Number(arg.warningTemperature)
          : Number(
              convertFahrToCent(Number(arg.warningTemperature)).toFixed(2)
            ),
    };

    await tireProfileHandler.setTireProfileLimits(payloadWithConversion);
  }

  async function saveAxleLimit(arg: any) {
    const payloadWithConversion = {
      ...arg,

      maxPressure:
        unitSystem === 'metric'
          ? Number(arg.maxPressure)
          : Number(convertPsiToKpa(Number(arg.maxPressure)).toFixed(2)),

      minPressure:
        unitSystem === 'metric'
          ? Number(arg.minPressure)
          : Number(convertPsiToKpa(Number(arg.minPressure)).toFixed(2)),

      warningPressure:
        unitSystem === 'metric'
          ? Number(arg.warningPressure)
          : Number(convertPsiToKpa(Number(arg.warningPressure)).toFixed(2)),

      maxTemperature:
        unitSystem === 'metric'
          ? Number(arg.maxTemperature)
          : Number(convertFahrToCent(Number(arg.maxTemperature)).toFixed(2)),

      minTemperature:
        unitSystem === 'metric'
          ? Number(arg.minTemperature)
          : Number(convertFahrToCent(Number(arg.minTemperature)).toFixed(2)),

      warningTemperature:
        unitSystem === 'metric'
          ? Number(arg.warningTemperature)
          : Number(
              convertFahrToCent(Number(arg.warningTemperature)).toFixed(2)
            ),
    };

    return await tireProfileHandler.setAxleWiseLimits(payloadWithConversion);
  }

  async function updateFitmentData(arg: any, tireCode: string) {
    await tireProfileHandler.setTireFitment(
      arg,
      selectedAsset.assetId,
      tireCode,
      selectedAsset?.device?.deviceId
    );
    handleEndDateTimeClose();
  }

  function handleUpdateTirePosition(arg: any) {
    void tireProfileHandler.updateTirePosition(arg, selectedAsset.assetId);
    setIsTirePositionOpen(false);
    handleEndDateTimeClose();
  }

  function handleSelectAsset(bumperNumber: string) {
    const item = tpmsAssets.find(
      (asset) => asset.bumperNumber === bumperNumber
    );
    if (item) {
      setSelectedAsset(item);
    }
  }

  const handleSelectMakeModel = (makeModel: string) => {
    try {
      const filteredAssets = tpmsAssets
        .filter(
          (asset) =>
            composeUniqueKey([asset.make, asset.model], '-') === makeModel
        )
        .map((item) => {
          return item.bumperNumber;
        })
        .sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
      setAssetBumperIds(filteredAssets);
      handleSelectAsset(filteredAssets[0]);
      setSelectedMakeModel(makeModel);
      setComparingAsset('');
    } catch (error) {
      console.log('Error while selecting make model!');
    }
  };

  const handleEndDateTimeClose = () => {
    const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
      'Custom',
      startDate,
      endDate,
      timezoneBasedOnDisplayMode
    );

    if (!startUtcISO || !endUtcISO) return;

    if (selectedAsset?.assetId !== '' && selectedAsset?.device?.deviceId) {
      void tireProfileHandler.getAssetTireData(
        selectedAsset.device.deviceId,
        selectedAsset.assetType,
        startUtcISO,
        endUtcISO,
        selectedAsset.assetId,
        selectedAsset.make,
        selectedAsset.model
      );
      clearProfile();

      // Fetch battery data for selected asset
      void tireProfileHandler.getBatteryDataForDevices([
        {
          deviceId: selectedAsset.device.deviceId,
          assetType: selectedAsset.assetType,
        },
      ]);
    }

    if (comparingAsset !== '') {
      const assetToFetch = tpmsAssets.find(
        (item) => item.bumperNumber === comparingAsset
      );
      if (assetToFetch) {
        void tireProfileHandler.getAssetTireData(
          assetToFetch.device.deviceId,
          assetToFetch.assetType,
          startUtcISO,
          endUtcISO,
          assetToFetch.assetId,
          assetToFetch.make,
          assetToFetch.model
        );
      }
    }
  };

  useEffect(() => {
    if (defaultAsset) {
      const defaultAssetDetails = tpmsAssets.find(
        (item) => item.bumperNumber === defaultAsset
      );

      if (defaultAssetDetails) {
        const assetMakeModel = composeUniqueKey(
          [defaultAssetDetails.make, defaultAssetDetails.model],
          '-'
        );

        const bumperIdsOfSelectedMakeModel = tpmsAssets
          ?.filter(
            (asset) =>
              composeUniqueKey([asset.make, asset.model], '-') ===
              assetMakeModel
          )
          ?.map((asset) => asset.bumperNumber)
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );

        setSelectedAsset(defaultAssetDetails);
        setSelectedMakeModel(assetMakeModel);
        setAssetBumperIds(bumperIdsOfSelectedMakeModel);
      }
    } else {
      if (tpmsAssets.length > 0) {
        const assetWithMostTires = findDeviceWithMostTpmSData(fleetData);
        const topAsset =
          tpmsAssets.find(
            (tpmsAsset) => tpmsAsset?.device.deviceId === assetWithMostTires
          ) ?? tpmsAssets[0];
        const topAssetMakeModel = composeUniqueKey(
          [topAsset.make, topAsset.model],
          '-'
        );
        const bumperIdsOfSelectedMakeModel = tpmsAssets
          ?.filter(
            (asset) =>
              composeUniqueKey([asset.make, asset.model], '-') ===
              topAssetMakeModel
          )
          ?.map((asset) => asset?.bumperNumber)
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );
        setSelectedAsset(topAsset);
        setSelectedMakeModel(topAssetMakeModel);
        setAssetBumperIds(bumperIdsOfSelectedMakeModel);
      }
    }
    setComparingAsset('');
  }, [defaultAsset, tpmsAssets, fleetData]);

  useEffect(() => {
    if (accessToken) {
      void tireProfileHandler.getFleetTireProfile();
    }
  }, []);

  // Fetch axle-wise limits (per tireCode thresholds) once we have auth + customer
  useEffect(() => {
    if (accessToken && customerId) {
      void tireProfileHandler.getAxleWiseLimits(customerId);
    }
  }, [accessToken, customerId]);

  useEffect(() => {
    if (!accessToken) return;
    if (!timezoneBasedOnDisplayMode) return;

    const isFirstLoad = initialPageLoad && defaultDate;
    const isCustomRange = selectedRange === 'Custom';

    const shouldFetchMainAsset = !isCustomRange || isFirstLoad;

    let currentStartDate;
    let currentEndDate;

    if (!isFirstLoad && !isCustomRange) {
      const { startRangeDate, endRangeDate }: any =
        getRangeDates(selectedRange);
      setStartDate(startRangeDate);
      setEndDate(endRangeDate);

      const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
        selectedRange,
        null,
        null,
        timezoneBasedOnDisplayMode
      );

      currentStartDate = startUtcISO;
      currentEndDate = endUtcISO;
    } else {
      const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
        'Custom',
        startDate,
        endDate,
        timezoneBasedOnDisplayMode
      );

      currentStartDate = startUtcISO;
      currentEndDate = endUtcISO;
    }

    if (!currentStartDate || !currentEndDate) return;

    let madeRequest = false;

    if (
      shouldFetchMainAsset &&
      selectedAsset?.assetId !== '' &&
      selectedAsset?.device?.deviceId
    ) {
      void tireProfileHandler.getAssetTireData(
        selectedAsset.device.deviceId,
        selectedAsset.assetType,
        currentStartDate,
        currentEndDate,
        selectedAsset.assetId,
        selectedAsset.make,
        selectedAsset.model
      );
      // Fetch battery data for selected asset
      void tireProfileHandler.getBatteryDataForDevices([
        {
          deviceId: selectedAsset.device.deviceId,
          assetType: selectedAsset.assetType,
        },
      ]);
      madeRequest = true;
    }

    if (comparingAsset !== '') {
      const assetToFetch = tpmsAssets.find(
        (item) => item.bumperNumber === comparingAsset
      );
      if (assetToFetch) {
        void tireProfileHandler.getAssetTireData(
          assetToFetch.device.deviceId,
          assetToFetch.assetType,
          currentStartDate,
          currentEndDate,
          assetToFetch.assetId,
          assetToFetch.make,
          assetToFetch.model
        );
        madeRequest = true;
      }
    }

    if (initialPageLoad && madeRequest) {
      setInitialPageLoad(false);
    }
  }, [
    selectedAsset,
    selected,
    accessToken,
    selectedRange,
    comparingAsset,
    timezoneBasedOnDisplayMode,
  ]);

  const intervalRef = useRef<any>(null);

  useEffect(() => {
    if (!timezoneBasedOnDisplayMode) {
      // clearing interval incase of no tz
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!selectedAsset?.device?.deviceId || !selectedAsset?.assetType) return;

    intervalRef.current = setInterval(() => {
      const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
        'Custom',
        startDate,
        endDate,
        timezoneBasedOnDisplayMode
      );

      if (!startUtcISO || !endUtcISO) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        return;
      }

      void tireProfileHandler.getAssetTireData(
        selectedAsset.device.deviceId,
        selectedAsset.assetType,
        startUtcISO,
        endUtcISO,
        selectedAsset.assetId,
        selectedAsset.make,
        selectedAsset.model,
        { silent: true }
      );

      // Fetch battery data for selected asset
      void tireProfileHandler.getBatteryDataForDevices([
        {
          deviceId: selectedAsset.device.deviceId,
          assetType: selectedAsset.assetType,
        },
      ]);
    }, 5 * 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedAsset, startDate, endDate, timezoneBasedOnDisplayMode]);

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const assetsWithDevices = tpmsAssets.filter((item) => item.device);

  return (
    <>
      {!assetsLoaded || isLoadingVehicle ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <SingleFadeLoader />
        </div>
      ) : tpmsAssets.length > 0 ? (
        <>
          {isSelectModalOpen && (
            <PressureLimitModalTabularView
              open={isSelectModalOpen}
              handleAbort={() => {
                setIsSelectModalOpen(false);
              }}
              enableDarkTheme={enableDarkTheme}
              assets={assetsWithDevices}
              updatePressureLimitForRow={addPressureLimit}
              defaultMetricType={metricType}
            />
          )}

          {isAxleWiseModalOpen && (
            <AxleWiseThresholdsModal
              open={isAxleWiseModalOpen}
              handleAbort={() => {
                setIsAxleWiseModalOpen(false);
              }}
              enableDarkTheme={enableDarkTheme}
              customerId={customerId}
              saveAxleLimit={saveAxleLimit}
              initialAssetId={selectedAsset?.assetId}
            />
          )}

          {selectedAsset?.device?.deviceId && isFitmentViewOpen && (
            <FitmentDate
              open={isFitmentViewOpen}
              handleAbort={() => {
                setIsFitmentViewOpen(false);
              }}
              title={`${selectedAsset.bumperNumber} - ${t(
                'set_fitment_date_for_tires'
              )}`}
              enableDarkTheme={enableDarkTheme}
              selectedAsset={selectedAsset}
              tireData={tireData}
              updateFitmentData={updateFitmentData}
              makeModelDetails={makeModelDetails}
              tirePatterns={tirePatterns}
              customerId={customerId}
            />
          )}

          {selectedAsset?.device?.deviceId && isTirePositionOpen && (
            <TirePosition
              open={isTirePositionOpen}
              handleAbort={() => {
                setIsTirePositionOpen(false);
              }}
              title={`${selectedAsset.bumperNumber} - ${t(
                'update_position_for_tires'
              )}`}
              enableDarkTheme={enableDarkTheme}
              selectedAsset={selectedAsset}
              tireData={tireData}
              updateTirePosition={handleUpdateTirePosition}
              makeModelDetails={makeModelDetails}
              tirePatterns={tirePatterns}
              customerId={customerId}
            />
          )}
          <Box>
            <>
              {!selectedAsset ? (
                <Box
                  sx={{
                    marginTop: '20px',
                    backgroundColor: enableDarkTheme
                      ? palette.toolBarBackground
                      : palette.toolBarBackground,
                    padding: '10px',
                    borderRadius: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      color: palette.textPrimary,
                    }}
                  >
                    {t('no_asset_or_asset_details_unavailable')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      marginTop: '20px',
                      backgroundColor: enableDarkTheme
                        ? palette.toolBarBackground
                        : palette.toolBarBackground,
                      padding: '10px',
                      borderRadius: '4px',
                      display: 'flex',
                    }}
                  >
                    <AssetSelector
                      makeModels={makeModels}
                      enableDarkTheme={enableDarkTheme}
                      selectedMakeModel={selectedMakeModel}
                      setSelectedMakeModel={handleSelectMakeModel}
                      assetBumperIds={assetBumperIds}
                      selectedAsset={selectedAsset}
                      setSelectedAsset={handleSelectAsset}
                      comparingAsset={comparingAsset}
                      setComparingAsset={setComparingAsset}
                    />
                  </Box>
                  <Box
                    sx={{
                      marginTop: '20px',
                      backgroundColor: enableDarkTheme
                        ? palette.toolBarBackground
                        : palette.toolBarBackground,
                      padding: '10px',
                      borderRadius: '4px',
                    }}
                  >
                    <AssetDetail
                      enableDarkTheme={enableDarkTheme}
                      selectedAsset={selectedAsset}
                      latestTagData={latestTagData}
                      hierarchyInfo={hierarchyInfo}
                      setIsSelectModalOpen={setIsSelectModalOpen}
                      setIsAxleWiseModalOpen={setIsAxleWiseModalOpen}
                      setIsFitmentViewOpen={setIsFitmentViewOpen}
                      setIsTirePositionOpen={setIsTirePositionOpen}
                      metricType={metricType}
                      tireData={tireData}
                      batteryData={batteryData}
                    />
                  </Box>

                  <Content
                    enableDarkTheme={enableDarkTheme}
                    selected={selected}
                    setSelected={setSelected}
                    limits={axleWiseLimits}
                    tireData={tireData}
                    selectedAsset={selectedAsset}
                    startDate={startDate}
                    endDate={endDate}
                    unitSystem={unitSystem}
                    metricType={metricType}
                    setMetricType={setMetricType}
                    comparingAsset={comparingAsset}
                    assets={tpmsAssets}
                    callOnFinalDateChange={handleEndDateTimeClose}
                    selectedRange={selectedRange}
                    setSelectedRange={setSelectedRange}
                    handleDateChange={handleDateChange}
                    latestTagData={latestTagData}
                    makeModelDetails={makeModelDetails}
                    tirePatterns={tirePatterns}
                    customerId={customerId}
                    batteryData={batteryData}
                    defaultProfile={defaultProfile}
                    clearProfile={clearProfile}
                    defaultTireCode={defaultTireCode}
                  />
                </>
              )}
            </>
          </Box>
        </>
      ) : (
        <Typography
          sx={{
            color: palette.textPrimary,
            padding: '10px',
          }}
        >
          {' '}
          {t('please_ensure_tpms_enabled_for_assets')}
        </Typography>
      )}
    </>
  );
};

export default VehicleInsight;
